// src/utils/draftUtils.js

import {
  collection,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase"; // Import Firestore instance

/**
 * Function to save or update the draft in Firestore.
 * @param {Object} params - Parameters for saving the draft.
 * @param {string} params.submissionId - The ID of the submission.
 * @param {Object} params.currentUser - The current authenticated user.
 * @param {Object} params.draftPayload - The payload containing draft data.
 * @param {string} [params.draftIdStorageKey] - The key for storing the draft ID in localStorage.
 * @param {function} params.setSnackbarMessage - Function to set the snackbar message.
 * @param {string} params.customSnackbarMessage - Optional custom snackbar message.
 * @returns {Promise<void>} - A promise that resolves when the operation is complete.
 */
export const saveDraft = async ({
  submissionId,
  currentUser,
  draftPayload,
  draftIdStorageKey, // Optional
  setSnackbarMessage,
  customSnackbarMessage = "", // Optional
}) => {
  if (!currentUser) {
    setSnackbarMessage("Please sign in first");
    return;
  }

  try {
    if (submissionId) {
      // Update the existing draft
      const draftDocRef = doc(db, "submissions", submissionId);
      await updateDoc(draftDocRef, draftPayload);
      setSnackbarMessage(
        customSnackbarMessage
          ? customSnackbarMessage
          : "Draft updated successfully"
      );
    } else {
      // Create a new draft
      const submissionsCollectionRef = collection(db, "submissions");
      const newDocRef = await addDoc(submissionsCollectionRef, {
        ...draftPayload,
        createdAt: new Date(),
      });
      // Store the new draft ID in local storage if draftIdStorageKey is provided
      if (draftIdStorageKey) {
        localStorage.setItem(draftIdStorageKey, newDocRef.id);
      }
      setSnackbarMessage(
        customSnackbarMessage
          ? customSnackbarMessage
          : "Draft saved successfully"
      );
    }
  } catch (error) {
    console.error("Error saving draft:", error);
    // setSnackbarMessage("Failed to save draft");
    throw error; // Re-throw to allow further handling if necessary
  }
};

export const handleCreateNewSubmissionDraft = async (draftPayload) => {
  const submissionsCollection = collection(db, "submissions");
  const submissionDoc = await addDoc(submissionsCollection, {
    ...draftPayload,
    status: "draft",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    paymentData: {
      status: "INCOMPLETE",
    },
  });

  const submissionId = submissionDoc.id;

  return submissionId;
};
